import { Box, Container, Typography, Divider, Link } from "@mui/material";

const PrivacyPolicy = () => {
  return (
    <Container
      maxWidth="md"
      sx={{ py: 4 }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          Privacy Policy
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
        >
          Last Updated: February 15, 2026
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          1. Introduction
        </Typography>
        <Typography paragraph>
          Marketsupport Canada Inc. ("Marketsupport," "Company," "we," "us," or
          "our") operates the SaSSe (Retail Operations Management System)
          platform and related mobile applications (collectively, the
          "Services").
        </Typography>
        <Typography paragraph>
          This Privacy Policy describes how we collect, use, disclose, retain,
          and safeguard personal information in accordance with applicable
          Canadian privacy laws, including the Personal Information Protection
          and Electronic Documents Act (PIPEDA).
        </Typography>
        <Typography paragraph>
          By using our Services, you acknowledge that you have read and
          understood this Privacy Policy.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          2. Scope
        </Typography>
        <Typography paragraph>This Privacy Policy applies to:</Typography>
        <Box
          component="ul"
          sx={{ pl: 3, mb: 2 }}
        >
          <Typography
            component="li"
            paragraph
          >
            Employees and field representatives using the SaSSe platform
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Supervisors and managers accessing operational dashboards
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Individuals whose information is processed within the platform
          </Typography>
        </Box>
        <Typography paragraph>
          Personal information collected during the hiring process is governed
          by internal HR privacy practices and employment agreements.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          3. Information We Collect
        </Typography>

        <Typography
          variant="h6"
          sx={{ mt: 2, mb: 1, fontWeight: "bold" }}
        >
          3.1 Personal Information
        </Typography>
        <Typography paragraph>
          Personal information is primarily collected during the HR hiring and
          onboarding process. When you use the SaSSe mobile application, we
          process previously collected employment-related information,
          including:
        </Typography>
        <Box
          component="ul"
          sx={{ pl: 3, mb: 2 }}
        >
          <Typography
            component="li"
            paragraph
          >
            <strong>Account Information:</strong> Name, email address, phone
            number, username, and authentication credentials
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            <strong>Updatable Information:</strong> Email address, physical
            address, and password. Field representatives and supervisors may
            update certain account details within the application.
          </Typography>
        </Box>

        <Typography
          variant="h6"
          sx={{ mt: 2, mb: 1, fontWeight: "bold" }}
        >
          3.2 Operational and Employment Data
        </Typography>
        <Typography paragraph>
          In the course of field activities, we collect:
        </Typography>
        <Box
          component="ul"
          sx={{ pl: 3, mb: 2 }}
        >
          <Typography
            component="li"
            paragraph
          >
            Survey responses and questionnaire data
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Store visit observations
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Photos and images captured in-app
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Timestamps of visits and activity logs
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Notes and comments
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Performance metrics
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Territory assignments
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Location data (if enabled)
          </Typography>
        </Box>
        <Typography paragraph>
          This information is collected as part of employment responsibilities
          and business operations.
        </Typography>

        <Typography
          variant="h6"
          sx={{ mt: 2, mb: 1, fontWeight: "bold" }}
        >
          3.3 Device and Technical Information
        </Typography>
        <Typography paragraph>
          We automatically collect limited technical information, including:
        </Typography>
        <Box
          component="ul"
          sx={{ pl: 3, mb: 2 }}
        >
          <Typography
            component="li"
            paragraph
          >
            Device type
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Operating system
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Application version
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Mobile network information
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Crash logs and diagnostic data
          </Typography>
        </Box>
        <Typography paragraph>
          This information is used for system performance, security, and
          troubleshooting.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          4. How We Use Personal Information
        </Typography>
        <Typography paragraph>
          We use personal information for the following purposes:
        </Typography>
        <Box
          component="ul"
          sx={{ pl: 3, mb: 2 }}
        >
          <Typography
            component="li"
            paragraph
          >
            <strong>Service Delivery:</strong> Providing access to SaSSe
            platform and mobile applications; authenticating users
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            <strong>Territory &amp; Workforce Management:</strong> Assigning
            territories and store visits; tracking completion of required tasks
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            <strong>Business Operations &amp; Analytics:</strong> Analyzing
            operational performance, generating reports and business
            intelligence, and improving operational efficiency
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            <strong>Communication:</strong> Sending system notifications and
            operational updates
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            <strong>Security &amp; Risk Management:</strong> Detecting
            unauthorized access, preventing fraud or misuse, and maintaining
            system integrity
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            <strong>Legal &amp; Regulatory Compliance:</strong> Meeting legal
            obligations and supporting audit requirements
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          5. Data Ownership &amp; Employment Context
        </Typography>
        <Typography paragraph>
          All operational data collected through the SaSSe platform is business
          information and is the property of:
        </Typography>
        <Box
          component="ul"
          sx={{ pl: 3, mb: 2 }}
        >
          <Typography
            component="li"
            paragraph
          >
            Marketsupport Canada Inc., and/or
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            The applicable employing entity or client organization
          </Typography>
        </Box>
        <Typography paragraph>This includes:</Typography>
        <Box
          component="ul"
          sx={{ pl: 3, mb: 2 }}
        >
          <Typography
            component="li"
            paragraph
          >
            Survey responses
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Photos captured during store visits
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Call docket information
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Observations and notes recorded in call dockets
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Performance metrics and activity logs
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Territory and visit records
          </Typography>
        </Box>
        <Typography paragraph>
          Operational data is collected strictly for business purposes and may
          be used for reporting, performance evaluation, client deliverables,
          and strategic planning. Employees and field representatives do not
          retain personal ownership rights in operational business data created
          in the course of employment.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          6. Consent
        </Typography>
        <Typography paragraph>
          By using the SaSSe platform, you consent to the collection, use, and
          disclosure of personal information as described in this Privacy
          Policy.
        </Typography>
        <Typography paragraph>
          Where appropriate, consent may be implied by employment context or
          system usage. You may withdraw consent for certain optional data
          processing (such as location tracking), subject to employment
          obligations and operational requirements.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          7. Data Sharing and Disclosure
        </Typography>

        <Typography
          variant="h6"
          sx={{ mt: 2, mb: 1, fontWeight: "bold" }}
        >
          7.1 Internal Access
        </Typography>
        <Typography paragraph>
          We may share information internally with:
        </Typography>
        <Box
          component="ul"
          sx={{ pl: 3, mb: 2 }}
        >
          <Typography
            component="li"
            paragraph
          >
            Supervisors and managers
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Company leadership
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Authorized administrative personnel
          </Typography>
        </Box>
        <Typography paragraph>
          Access is limited to individuals who require information to perform
          their roles.
        </Typography>

        <Typography
          variant="h6"
          sx={{ mt: 2, mb: 1, fontWeight: "bold" }}
        >
          7.2 Service Providers
        </Typography>
        <Typography paragraph>
          We may share information with third-party service providers who
          support our operations, including:
        </Typography>
        <Box
          component="ul"
          sx={{ pl: 3, mb: 2 }}
        >
          <Typography
            component="li"
            paragraph
          >
            DigitalOcean (cloud hosting)
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            DigitalOcean Spaces (object storage)
          </Typography>
        </Box>
        <Typography paragraph>
          These providers process data under contractual safeguards and
          industry-standard security controls. Some data may be stored or
          processed outside of Canada. When this occurs, information may be
          subject to the laws of the jurisdiction in which it is processed.
        </Typography>

        <Typography
          variant="h6"
          sx={{ mt: 2, mb: 1, fontWeight: "bold" }}
        >
          7.3 We Do Not
        </Typography>
        <Box
          component="ul"
          sx={{ pl: 3, mb: 2 }}
        >
          <Typography
            component="li"
            paragraph
          >
            Sell personal information
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Share personal information for advertising purposes
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Disclose passwords or authentication credentials
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          8. Data Security
        </Typography>
        <Typography paragraph>
          We implement reasonable administrative, technical, and physical
          safeguards, including:
        </Typography>
        <Box
          component="ul"
          sx={{ pl: 3, mb: 2 }}
        >
          <Typography
            component="li"
            paragraph
          >
            AES-256 encryption of sensitive stored data
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            HTTPS encryption in transit
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Secured MongoDB databases with authentication controls
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Role-based access controls
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Regular security reviews and vulnerability assessments
          </Typography>
        </Box>
        <Typography paragraph>
          While we use commercially reasonable safeguards, no system can be
          guaranteed 100% secure.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          9. Data Retention
        </Typography>
        <Typography paragraph>
          We retain personal information only as long as necessary for:
        </Typography>
        <Box
          component="ul"
          sx={{ pl: 3, mb: 2 }}
        >
          <Typography
            component="li"
            paragraph
          >
            Employment and operational purposes
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Comply with legal and regulatory requirements
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Dispute resolution
          </Typography>
        </Box>
        <Typography paragraph>
          Upon termination of employment, system access is revoked. Personal
          data is retained in accordance with legal, contractual, and business
          requirements. We do not immediately delete employment records upon
          termination where retention is required by law or legitimate business
          needs.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          10. Mobile App &amp; Offline Data
        </Typography>
        <Typography paragraph>
          The mobile application stores data locally using an encrypted SQLite
          database to allow offline functionality.
        </Typography>
        <Box
          component="ul"
          sx={{ pl: 3, mb: 2 }}
        >
          <Typography
            component="li"
            paragraph
          >
            Local data is encrypted on your device
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Data synchronizes automatically when connectivity is restored
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Uninstalling the app deletes local data stored on the device
          </Typography>
        </Box>
        <Typography paragraph>
          Users are encouraged to synchronize before uninstalling.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          11. Permissions
        </Typography>

        <Typography
          variant="h6"
          sx={{ mt: 2, mb: 1, fontWeight: "bold" }}
        >
          Location
        </Typography>
        <Typography paragraph>
          Location tracking is optional but operationally important. Disabling
          location services may affect reporting accuracy and operational
          visibility.
        </Typography>

        <Typography
          variant="h6"
          sx={{ mt: 2, mb: 1, fontWeight: "bold" }}
        >
          Camera
        </Typography>
        <Typography paragraph>
          Camera access is required for job-related photo documentation. Photos
          must be captured within the app and cannot be uploaded from device
          galleries.
        </Typography>
        <Typography paragraph>
          Users may modify optional permissions via device settings.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          12. Your Privacy Rights (Canada)
        </Typography>
        <Typography paragraph>
          Under PIPEDA, you may have the right to:
        </Typography>
        <Box
          component="ul"
          sx={{ pl: 3, mb: 2 }}
        >
          <Typography
            component="li"
            paragraph
          >
            Access personal information we hold about you
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Request corrections to inaccurate information
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Withdraw consent (subject to legal and employment obligations)
          </Typography>
          <Typography
            component="li"
            paragraph
          >
            Request information about our data practices
          </Typography>
        </Box>
        <Typography paragraph>
          To exercise your rights, contact us at{" "}
          <Link href="mailto:it@marketsupport.ca">it@marketsupport.ca</Link>. We
          will respond within 30 days as required by applicable law.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          13. Accountability &amp; Privacy Officer
        </Typography>
        <Typography paragraph>
          Marketsupport Canada Inc. is responsible for personal information
          under its control. Questions, complaints, or privacy inquiries may be
          directed to:
        </Typography>
        <Typography paragraph>
          Privacy Officer
          <br />
          Marketsupport Canada Inc.
          <br />
          Email:{" "}
          <Link href="mailto:it@marketsupport.ca">it@marketsupport.ca</Link>
        </Typography>
        <Typography paragraph>
          If you are not satisfied with our response, you may contact the Office
          of the Privacy Commissioner of Canada.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          14. Changes to This Policy
        </Typography>
        <Typography paragraph>
          We may update this Privacy Policy periodically. Material changes will
          be posted with a revised "Last Updated" date.
        </Typography>
        <Typography paragraph>
          Continued use of the Services after updates constitutes acceptance of
          the revised policy.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography paragraph>
          If you have questions, concerns, or requests regarding this Privacy
          Policy or our privacy practices, contact us at{" "}
          <Link href="mailto:it@marketsupport.ca">it@marketsupport.ca</Link>.
        </Typography>
      </Box>

      <Box sx={{ mt: 6, pt: 4, borderTop: "1px solid #e0e0e0" }}>
        <Typography
          variant="body2"
          color="textSecondary"
          align="center"
        >
          © 2026 Marketsupport Canada Inc. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;
